import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../../css/AddParents.css";
import Loader from "../../components/Loader";

function AddParents() {
    const [searchQuery, setSearchQuery] = useState('');
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('pending');
    const [parentData, setParentData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        occupation: '',
        relationship: '',
        userCode: ''
    });

    // Replace the existing useEffect and fetchStudents function with this
    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Unauthorized! Please login again.");
                return;
            }

            const response = await fetch("https://schoolpro-api.uniums.com/api/students", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch students");
            }

            const data = await response.json();
            setStudents(data);
            setFilteredStudents(data);
        } catch (error) {
            console.error("Error fetching students:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);

        if (query.trim() === '') {
            setFilteredStudents(students);
        } else {
            const filtered = students.filter(student =>
                student.firstName.toLowerCase().includes(query) ||
                student.lastName.toLowerCase().includes(query) ||
                student.rollNumber.toLowerCase().includes(query) ||
                student.grade.toLowerCase().includes(query)
            );
            setFilteredStudents(filtered);
        }
    };

    // Handle student selection
    const handleStudentSelect = (student) => {
        setSelectedStudent(student);
        // Reset parent form when selecting a new student
        setParentData({
            firstName: student.parentName?.split(' ')[0] || '',
            lastName: student.parentName?.split(' ')[1] || '',
            email: student.email || '',
            phone: student.parentPhone || '',
            address: student.address || '',
            occupation: '',
            relationship: '',
            userCode: generateUserCode()
        });
    };

    // Generate a random user code
    const generateUserCode = () => {
        return 'PAR' + Math.floor(100000 + Math.random() * 900000);
    };

    // Handle parent form input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setParentData({
            ...parentData,
            [name]: value
        });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStudent) {
            toast.error("Please select a student first");
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Unauthorized! Please login again.");
                return;
            }

            const response = await fetch(`https://schoolpro-api.uniums.com/api/parents/add/${selectedStudent._id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(parentData),
            });

            const data = await response.json();
            if (response.ok) {
                toast.success("Parent assigned successfully");
                const updatedStudents = students.map(student =>
                    student._id === selectedStudent._id ? { ...student, hasParent: true } : student
                );
                setStudents(updatedStudents);
                setFilteredStudents(updatedStudents);
                setSelectedStudent(null);
            } else {
                toast.error(data.message || "Failed to assign parent");
            }
        } catch (error) {
            console.error("Error assigning parent:", error);
            toast.error("An error occurred while assigning parent");
        } finally {
            setIsLoading(false);
        }
    };

    // Filter students based on active tab (pending/completed)
    const displayStudents = filteredStudents.filter(student =>
        (activeTab === 'pending' && !student.hasParent) ||
        (activeTab === 'completed' && student.hasParent)
    );

    return (
        <>
            <div className="addparent-container">
                <h1 className="addparent-title">Assign Parents to Students</h1>
                {isLoading && <Loader />}

                <div className="addparent-tabs">
                    <button
                        className={`addparent-tab ${activeTab === 'pending' ? 'addparent-tab-active' : ''}`}
                        onClick={() => setActiveTab('pending')}
                    >
                        Pending Assignments
                    </button>
                    <button
                        className={`addparent-tab ${activeTab === 'completed' ? 'addparent-tab-active' : ''}`}
                        onClick={() => setActiveTab('completed')}
                    >
                        Completed Assignments
                    </button>
                </div>

                <div className="addparent-search-container">
                    <input
                        type="text"
                        placeholder="Search students by name, roll number, or grade..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="addparent-search-input"
                    />
                </div>

                <div className="addparent-content">
                    <div className="addparent-student-list">
                        <h2 className="addparent-section-title">
                            {activeTab === 'pending' ? 'Students Without Parents' : 'Students With Parents'}
                        </h2>
                        {displayStudents.length === 0 ? (
                            <div className="addparent-no-results">
                                {searchQuery ? 'No students match your search' : 'No students found in this category'}
                            </div>
                        ) : (
                            <div className="addparent-students-grid">
                                {displayStudents.map((student, index) => (
                                    <div
                                        key={student._id || `student-${index}`} // Changed from student.id to student._id
                                        className={`addparent-student-card ${selectedStudent?._id === student._id ? 'addparent-student-selected' : ''}`} // Changed from student.id to student._id
                                        onClick={() => activeTab === 'pending' ? handleStudentSelect(student) : null}
                                    >
                                        <div className="addparent-student-name">
                                            {student.firstName} {student.lastName}
                                        </div>
                                        <div className="addparent-student-details">
                                            <span>Grade: {student.grade}</span>
                                            <span>Roll #: {student.rollNumber}</span>
                                        </div>
                                        <div className="addparent-student-status">
                                            {student.hasParent ?
                                                <span className="addparent-status-complete">Parent Assigned</span> :
                                                <span className="addparent-status-pending">No Parent</span>
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedStudent && activeTab === 'pending' && (
                        <div className="addparent-form-container">
                            <h2 className="addparent-section-title">Assign Parent to {selectedStudent.firstName} {selectedStudent.lastName}</h2>

                            <form onSubmit={handleSubmit} className="addparent-form">
                                <div className="addparent-form-section">
                                    <h3 className="addparent-subsection-title">Parent Information</h3>

                                    <div className="addparent-form-row">
                                        <div className="addparent-form-group">
                                            <label className="addparent-label">First Name*</label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={parentData.firstName}
                                                onChange={handleInputChange}
                                                className="addparent-input"
                                                required
                                            />
                                        </div>
                                        <div className="addparent-form-group">
                                            <label className="addparent-label">Last Name*</label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={parentData.lastName}
                                                onChange={handleInputChange}
                                                className="addparent-input"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="addparent-form-row">
                                        <div className="addparent-form-group">
                                            <label className="addparent-label">Email*</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={parentData.email}
                                                onChange={handleInputChange}
                                                className="addparent-input"
                                                required
                                            />
                                        </div>
                                        <div className="addparent-form-group">
                                            <label className="addparent-label">Phone*</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={parentData.phone}
                                                onChange={handleInputChange}
                                                className="addparent-input"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="addparent-form-group">
                                        <label className="addparent-label">Address*</label>
                                        <textarea
                                            name="address"
                                            value={parentData.address}
                                            onChange={handleInputChange}
                                            className="addparent-textarea"
                                            required
                                        ></textarea>
                                    </div>

                                    <div className="addparent-form-row">
                                        <div className="addparent-form-group">
                                            <label className="addparent-label">Occupation</label>
                                            <input
                                                type="text"
                                                name="occupation"
                                                value={parentData.occupation}
                                                onChange={handleInputChange}
                                                className="addparent-input"
                                            />
                                        </div>
                                        <div className="addparent-form-group">
                                            <label className="addparent-label">Relationship to Student*</label>
                                            <select
                                                name="relationship"
                                                value={parentData.relationship}
                                                onChange={handleInputChange}
                                                className="addparent-select"
                                                required
                                            >
                                                <option value="">Select Relationship</option>
                                                <option value="father">Father</option>
                                                <option value="mother">Mother</option>
                                                <option value="guardian">Guardian</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="addparent-form-section">
                                    <h3 className="addparent-subsection-title">Login Information</h3>
                                    <div className="addparent-form-group">
                                        <label className="addparent-label">User Code*</label>
                                        <div className="addparent-user-code-container">
                                            <input
                                                type="text"
                                                name="userCode"
                                                value={parentData.userCode}
                                                onChange={handleInputChange}
                                                className="addparent-input"
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="addparent-generate-btn"
                                                onClick={() => setParentData({ ...parentData, userCode: generateUserCode() })}
                                            >
                                                Generate
                                            </button>
                                        </div>
                                        <p className="addparent-helper-text">
                                            This code will be used by the parent to log in to the system.
                                        </p>
                                    </div>
                                </div>

                                <div className="addparent-button-container">
                                    <button
                                        type="button"
                                        className="addparent-cancel-btn"
                                        onClick={() => setSelectedStudent(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="addparent-submit-btn">
                                        Assign Parent
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === 'completed' && !selectedStudent && (
                        <div className="addparent-info-container">
                            <div className="addparent-info-card">
                                <h3>Parent Assignment Status</h3>
                                <div className="addparent-stats">
                                    <div className="addparent-stat-item">
                                        <span className="addparent-stat-value">{students.filter(s => s.hasParent).length}</span>
                                        <span className="addparent-stat-label">Parents Assigned</span>
                                    </div>
                                    <div className="addparent-stat-item">
                                        <span className="addparent-stat-value">{students.filter(s => !s.hasParent).length}</span>
                                        <span className="addparent-stat-label">Pending Assignments</span>
                                    </div>
                                    <div className="addparent-stat-item">
                                        <span className="addparent-stat-value">{students.length}</span>
                                        <span className="addparent-stat-label">Total Students</span>
                                    </div>
                                </div>
                                <p className="addparent-info-text">
                                    Click on a student card to view more details and perform actions.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ToastContainer />
        </>
    );
}

export default AddParents;